import React, { useState, FunctionComponent } from 'react';
import { isNaN, isFinite } from 'lodash';
import { Box, Grid } from '@mui/material';
import { DecimalMenu } from 'spw-programming-projects/program-summary/components/decimal-menu';
import {
  AsteriskContainer,
  BoldValue,
  SeatTypeMixBox,
  SeatTypeMixCategoryName,
  SeatTypeMixColumn,
  SeatTypeMixHeader,
  SeatTypeMixHeaderRow,
} from 'spw-programming-projects/program-summary/macro-single-page-summary/styles';
import { colors } from 'spw-theme';
import { EmptyListMessage } from 'spw-components/project-details-tabs/program-summary/shared/empty-list-message';
import {
  CardLegend,
  EmptyListBoxWrapper,
} from 'spw-components/project-details-tabs/program-summary/shared/styles';
import { spaceFactorMap as rawSpaceTypeFactorMap } from 'spw-programming-projects/program-summary/macro-single-page-summary/utils';
import {
  IProjectColors,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import {
  asPercentage,
  asPercentageRounded,
  getCustomSpaceColor,
  numberWithCommas,
  removeLineBreaks,
} from 'spw-shared/utils';
import {
  Category,
  IProjectSpaceType,
  Subcategory,
} from 'spw-redux/modules/space_types/types';
import { NO_DATA_TO_SHOW } from 'spw-constants';
import { SmallBubbleIcon } from 'spw-components/charts/styles';
import { PSCardGroupHeader } from 'spw-shared/globalComponents';
import { Column } from 'spw-shared/globalComponents/grid';
import { SpaceIcon } from 'spw-projects/components/space-type-icons';
import { useWorksheetRowsByCategory } from './hooks/use-worksheet-rows-by-category';
import { CategorySeatTypeMixSubtotalsProps } from '../program-summary-config';
import { CategorySeatTypeMixSubtotalsChildRow } from './category-seat-type-mix-subtotals-child-row';
import WorksheetRow from '../../../../models/WorksheetRow';
import {
  Card,
  CardTitle,
  RowContainer,
  SpaceTypeGroupHeaderContainer,
  SpaceTypeHeaderValue,
  SubcategoryContainer,
} from './style';

const SeatTypeMixSubtotalsRow = ({
  index,
  row,
  categoryOrSubcategory,
  showDecimals,
  projectColors,
  totalSeats,
  open,
  spaceTypeSeats,
  spaceTypeRatio,
  onClickSpaceIcon,
  projectSpaceTypes,
  viewOnly,
  clientFacingNamesAreVisible,
}: {
  index: number;
  row: WorksheetRow;
  categoryOrSubcategory: Category | Subcategory;
  showDecimals?: boolean;
  projectColors: IProjectColors;
  totalSeats: number;
  spaceTypeSeats: number;
  open?: boolean;
  spaceTypeRatio: number;
  onClickSpaceIcon?: (e: Element) => void;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  viewOnly: boolean;
  clientFacingNamesAreVisible?: boolean;
}) => {
  const spaceTypeFactorMap = rawSpaceTypeFactorMap(projectColors);
  if (row.child && !open) return null;
  if (row.child) {
    return (
      <CategorySeatTypeMixSubtotalsChildRow
        row={row}
        totalSeats={totalSeats}
        showDecimals={showDecimals}
        shouldShowClientFacingName={clientFacingNamesAreVisible}
      />
    );
  }
  const testingPrefixForRow = `programming_ps_seat_type_mix_subtotals_row_${row?.spaceType?.id}`;

  if (row.spaceType) {
    const seats = spaceTypeSeats || 0;
    const spaceTypeData = spaceTypeFactorMap[row.spaceType.spaceType];
    const rowIcon =
      projectSpaceTypes[row.projectSpaceType?.id]?.icon || row.spaceType?.icon;

    let color = getCustomSpaceColor(
      categoryOrSubcategory,
      row.spaceType.spaceType,
      projectColors
    );

    if (spaceTypeData) {
      color = spaceTypeData.color;
    }

    const seatsPercent = totalSeats ? seats / totalSeats : 0;
    let seatsPercentValue = '0%';

    if (!isNaN(seatsPercent) && isFinite(seatsPercent)) {
      seatsPercentValue = showDecimals
        ? asPercentage(seatsPercent)
        : asPercentageRounded(seatsPercent, false);
    }

    const projectSpaceTypeCategory = row.projectSpaceType?.category;

    const clientFacingName = row.projectSpaceType?.clientFacingName;

    const getSpaceTypeName = () => {
      if (clientFacingName && clientFacingNamesAreVisible)
        return clientFacingName;
      if (spaceTypeData) return spaceTypeData.title.toUpperCase();
      return row.spaceType.name.toUpperCase();
    };

    return (
      <RowContainer className={open && index !== 0 ? 'show-border-top' : ''}>
        <Grid container>
          <SeatTypeMixCategoryName item xs={12} className="borderless">
            <div
              data-cy={`programming_ps_seat_type_mix_subtotals_row_${row?.spaceType?.name}`}
              data-testid={`${testingPrefixForRow}_spaceTypeName`}
            >
              <pre>{removeLineBreaks(getSpaceTypeName())}</pre>
            </div>
          </SeatTypeMixCategoryName>
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            <Grid container style={{ paddingBottom: '8px' }}>
              <SeatTypeMixColumn item xs={4}>
                <Box display="flex" flexDirection="row" flexWrap="nowrap">
                  <Box>
                    <SpaceIcon
                      disabled={viewOnly}
                      projectSpaceTypeCategoryId={projectSpaceTypeCategory.id}
                      topLevelProjectSpaceTypeCategoryId={
                        (projectSpaceTypeCategory as IProjectSpaceTypeSubcategory)
                          ?.parentId || projectSpaceTypeCategory.id
                      }
                      projectSpaceTypeId={row.projectSpaceType?.id}
                      onClick={onClickSpaceIcon}
                      iconName={rowIcon}
                      defaultIconName={row.spaceType?.icon}
                      name={
                        spaceTypeData ? spaceTypeData.title : row.spaceType.name
                      }
                    />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <SmallBubbleIcon color={color as string} />
                  </Box>
                </Box>
              </SeatTypeMixColumn>
              <SeatTypeMixColumn item xs={4}>
                <SeatTypeMixBox
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  flexDirection="column"
                >
                  <Box>
                    <BoldValue data-testid={`${testingPrefixForRow}_seats`}>
                      {numberWithCommas(seats, showDecimals, true)}
                      {!row.isIncludedInTotalWorkSeats && (
                        <AsteriskContainer>*</AsteriskContainer>
                      )}
                    </BoldValue>
                  </Box>
                  <Box>Seats</Box>
                </SeatTypeMixBox>
              </SeatTypeMixColumn>
              <SeatTypeMixColumn item xs={4}>
                <SeatTypeMixBox
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="flex-end"
                  flexDirection="column"
                >
                  <Box>
                    <BoldValue data-testid={`${testingPrefixForRow}_seatMix`}>
                      {row.isIncludedInTotalWorkSeats
                        ? seatsPercentValue
                        : `1: ${numberWithCommas(
                            spaceTypeRatio,
                            showDecimals,
                            true
                          )}`}
                    </BoldValue>
                  </Box>
                  <Box>
                    {row.isIncludedInTotalWorkSeats
                      ? 'Seat Mix %'
                      : 'Target Ratio'}
                  </Box>
                </SeatTypeMixBox>
              </SeatTypeMixColumn>
            </Grid>
          </Grid>
        </Grid>
      </RowContainer>
    );
  }

  return null;
};

export const CategorySeatTypeMixSubtotals: FunctionComponent<CategorySeatTypeMixSubtotalsProps> = ({
  projectSpaceTypeCategory,
  projectColors,
  worksheet,
  open,
  isRenderingForExport,
  testingPrefix,
  onClickSpaceIcon,
  projectSpaceTypes,
  viewOnly,
  clientFacingNamesAreVisible,
}: CategorySeatTypeMixSubtotalsProps) => {
  const [showDecimals, setShowDecimals] = useState(false);
  const { category } = projectSpaceTypeCategory.defaultSpaceTypeCategory;
  const { rowsBySubcategory } = worksheet;

  const {
    categoryRows,
    hasRowsBelongingToASubcategory,
  } = useWorksheetRowsByCategory(projectSpaceTypeCategory, worksheet, open);

  return (
    <Card>
      <Box display="flex" flexDirection="column" style={{ height: '100%' }}>
        <CardTitle>
          <div
            style={{ width: 'calc(100% - 40px)' }}
            data-testid={`${testingPrefix}_title`}
          >
            {projectSpaceTypeCategory.name.toUpperCase()} SEAT TYPE MIX
          </div>
          {!isRenderingForExport && (
            <DecimalMenu
              onShowDecimals={(showDecimalVal: boolean) =>
                setShowDecimals(showDecimalVal)
              }
            />
          )}
        </CardTitle>
        <Grid container>
          <SeatTypeMixHeaderRow item xs={12}>
            <SeatTypeMixHeader>
              <SeatTypeMixColumn item xs={4}>
                <Grid container>
                  <Grid item xs={12} style={{ paddingTop: 16 }}>
                    Subtotals
                  </Grid>
                </Grid>
              </SeatTypeMixColumn>
              <SeatTypeMixColumn item xs={4}>
                <Grid
                  container
                  style={{ height: '100%', textAlign: 'right' }}
                  justifyContent="flex-end"
                  alignItems="flex-end"
                >
                  <Grid item xs={12}>
                    Seats
                  </Grid>
                </Grid>
              </SeatTypeMixColumn>
              <SeatTypeMixColumn item xs={4}>
                <Grid
                  container
                  style={{ height: '100%', textAlign: 'right' }}
                  justifyContent="flex-end"
                  alignItems="flex-end"
                >
                  <Grid item xs={12}>
                    Seat Mix %
                  </Grid>
                </Grid>
              </SeatTypeMixColumn>
            </SeatTypeMixHeader>
          </SeatTypeMixHeaderRow>
        </Grid>
        <PSCardGroupHeader
          className={`${
            projectSpaceTypeCategory.subcategories?.length > 0 &&
            !categoryRows.length
              ? 'mb-8'
              : ''
          }`}
          color={colors.veryLightGray}
        >
          <Box display="flex" alignItems="center">
            <span data-testid={`${testingPrefix}_individualWorkspaces_name`}>
              {projectSpaceTypeCategory.name.toUpperCase()}
            </span>
          </Box>
        </PSCardGroupHeader>
        <Column
          style={
            !hasRowsBelongingToASubcategory
              ? { flexGrow: 1, justifyContent: 'center' }
              : {}
          }
        >
          {categoryRows.length
            ? categoryRows.map((row: WorksheetRow, index: number) => {
                const categorySeats =
                  worksheet.categoriesTotals[category].seats;
                return (
                  <SeatTypeMixSubtotalsRow
                    viewOnly={viewOnly}
                    projectSpaceTypes={projectSpaceTypes}
                    onClickSpaceIcon={onClickSpaceIcon}
                    key={`${row.id}`}
                    index={index}
                    row={row}
                    categoryOrSubcategory={category}
                    showDecimals={showDecimals}
                    projectColors={projectColors}
                    totalSeats={categorySeats}
                    spaceTypeSeats={
                      worksheet.spaceTypesTotals[row.uniqueKey].seats
                    }
                    spaceTypeRatio={
                      worksheet.spaceTypesTotals[row.uniqueKey]?.ratio
                    }
                    clientFacingNamesAreVisible={clientFacingNamesAreVisible}
                  />
                );
              })
            : !hasRowsBelongingToASubcategory && (
                <EmptyListBoxWrapper>
                  <EmptyListMessage />
                </EmptyListBoxWrapper>
              )}
        </Column>

        {projectSpaceTypeCategory.subcategories?.length > 0 && (
          <SubcategoryContainer>
            {projectSpaceTypeCategory.subcategories?.map(
              (subcategory: IProjectSpaceTypeSubcategory, index: number) => {
                const {
                  category: defaultSubcategory,
                } = subcategory.defaultSpaceTypeCategory;
                const subcategorySeats =
                  worksheet.subcategoriesTotals[defaultSubcategory]?.seats;

                let subcategorySeatsPercentValue = `${NO_DATA_TO_SHOW}%`;
                if (!isNaN(subcategorySeats)) {
                  subcategorySeatsPercentValue = showDecimals
                    ? asPercentage(1)
                    : asPercentageRounded(1);
                }
                const subcategoryTotalQuantity =
                  worksheet.subcategoriesTotals[
                    subcategory.defaultSpaceTypeCategory.category
                  ]?.spacesQuantity;
                const ratio = subcategoryTotalQuantity
                  ? worksheet.scenario.workSeats / subcategoryTotalQuantity
                  : 0;

                return (
                  rowsBySubcategory[defaultSubcategory]?.length > 0 && (
                    <div key={`${subcategory.id}`}>
                      <PSCardGroupHeader>
                        <SpaceTypeGroupHeaderContainer>
                          <Grid item xs={6}>
                            <div>
                              <span
                                data-testid={`${testingPrefix}_subcat_row${index}_name`}
                              >
                                {subcategory.name.toUpperCase()}
                              </span>
                            </div>
                          </Grid>
                          <Grid item xs={2}>
                            <SpaceTypeHeaderValue>
                              <span
                                data-testid={`${testingPrefix}_subcat_row${index}_seats`}
                              >
                                {numberWithCommas(
                                  subcategorySeats,
                                  showDecimals,
                                  true
                                )}
                              </span>
                              {!subcategory.includeInTotalWorkSeats && (
                                <AsteriskContainer style={{ top: 0 }}>
                                  *
                                </AsteriskContainer>
                              )}
                            </SpaceTypeHeaderValue>
                          </Grid>
                          <Grid item xs={4}>
                            <SpaceTypeHeaderValue>
                              <span
                                data-testid={`${testingPrefix}_subcat_row${index}_seatMix`}
                              >
                                {subcategory.includeInTotalWorkSeats
                                  ? subcategorySeatsPercentValue
                                  : `1: ${numberWithCommas(
                                      ratio,
                                      showDecimals,
                                      true
                                    )}`}
                              </span>
                            </SpaceTypeHeaderValue>
                          </Grid>
                        </SpaceTypeGroupHeaderContainer>
                      </PSCardGroupHeader>
                      {rowsBySubcategory[defaultSubcategory].map(
                        (row: WorksheetRow, innerIndex: number) => {
                          return (
                            <SeatTypeMixSubtotalsRow
                              viewOnly={viewOnly}
                              projectSpaceTypes={projectSpaceTypes}
                              onClickSpaceIcon={onClickSpaceIcon}
                              key={`${row.id}`}
                              index={innerIndex}
                              row={row}
                              categoryOrSubcategory={defaultSubcategory}
                              showDecimals={showDecimals}
                              projectColors={projectColors}
                              totalSeats={subcategorySeats}
                              open={open}
                              spaceTypeSeats={
                                worksheet.spaceTypesTotals[row.uniqueKey]?.seats
                              }
                              spaceTypeRatio={
                                worksheet.spaceTypesTotals[row.uniqueKey]?.ratio
                              }
                              clientFacingNamesAreVisible={
                                clientFacingNamesAreVisible
                              }
                            />
                          );
                        }
                      )}
                    </div>
                  )
                );
              }
            )}
          </SubcategoryContainer>
        )}
        {worksheet.hasWorkspacesNotIncludedInTotalSeats && (
          <CardLegend>* Not included in Total Work Seats</CardLegend>
        )}
      </Box>
    </Card>
  );
};
